import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const SLIDE_DURATION_MS = 5000;

export const HeroSlideshow = ({ products = [] }) => {
  const { t, lang } = useLanguage();
  const slides = products.slice(0, 4);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (slides.length === 0) return;
    const id = setInterval(() => {
      setActive((p) => (p + 1) % slides.length);
    }, SLIDE_DURATION_MS);
    return () => clearInterval(id);
  }, [slides.length]);

  if (slides.length === 0) return null;

  return (
    <section className="relative overflow-hidden bg-[#F9F8F6]" data-testid="hero-slideshow">
      {/* Background slideshow */}
      <div className="absolute inset-0 -z-10">
        {slides.map((p, i) => (
          <div
            key={p.id}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              i === active ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={i !== active}
          >
            <img
              src={p.image_url}
              alt=""
              className={`w-full h-full object-cover ${
                i === active ? "hero-kenburns" : ""
              }`}
            />
          </div>
        ))}
        {/* Soft warm overlay so text stays readable */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#F9F8F6]/55 via-[#F9F8F6]/70 to-[#F9F8F6]"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#F9F8F6]/85 via-[#F9F8F6]/40 to-transparent"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-10 md:pt-24 pb-16 md:pb-32 relative">
        <div className="grid lg:grid-cols-[1fr_360px] gap-8 lg:gap-16 lg:items-end">
          {/* MOBILE — product card on top */}
          <div className="lg:hidden order-first mb-2">
            <div className="relative aspect-[16/10] max-w-sm mx-auto">
              {slides.map((p, i) => {
                const title = lang === "en" ? p.title_en : p.title_no;
                return (
                  <Link
                    key={p.id}
                    to={`/produkt/${p.slug}`}
                    data-testid={`hero-product-card-mobile-${i}`}
                    aria-hidden={i !== active}
                    className={`absolute inset-0 group transition-all duration-700 ease-out ${
                      i === active
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 translate-y-4 pointer-events-none"
                    }`}
                  >
                    <div className="relative h-full bg-white border border-[#E5E2DC] rounded-2xl overflow-hidden shadow-[0_12px_40px_-15px_rgba(44,48,46,0.25)]">
                      <img
                        src={p.image_url}
                        alt={title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#2C302E]/85 via-[#2C302E]/30 to-transparent" />
                      <span className="absolute top-3 left-3 bg-white/95 backdrop-blur rounded-full px-2.5 py-1 text-[9px] uppercase tracking-[0.2em] font-semibold text-[#D97B66]">
                        {lang === "en" ? "Now showing" : "Vises nå"}
                      </span>
                      <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-3">
                        <p className="font-heading text-white text-base font-medium leading-snug line-clamp-2 drop-shadow">
                          {title}
                        </p>
                        <span className="bg-[#D97B66] text-white font-heading text-sm font-medium rounded-full px-3 py-1 whitespace-nowrap">
                          {p.price_nok} NOK
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Left — copy */}
          <div className="max-w-2xl">
            <div className="fade-up inline-flex items-center gap-3 mb-8">
              <span className="w-8 h-px bg-[#D97B66]"></span>
              <span className="text-xs uppercase tracking-[0.25em] text-[#6B726C] font-medium">
                {t("hero_eyebrow")}
              </span>
            </div>

            <h1 className="fade-up-delay-1 font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tight font-light text-[#2C302E] leading-[1.05]">
              {t("hero_title_1")}{" "}
              <em className="not-italic font-medium text-[#D97B66]">
                {t("hero_title_2")}
              </em>
              <br />
              {t("hero_title_3")}
            </h1>

            <p className="fade-up-delay-2 mt-8 text-lg md:text-xl text-[#6B726C] leading-relaxed max-w-2xl">
              {t("hero_subtitle")}
            </p>

            <div className="fade-up-delay-3 mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/butikk"
                data-testid="hero-shop-cta"
                className="group bg-[#2C302E] hover:bg-[#1f221f] text-white rounded-full pl-7 pr-3 py-3 font-medium transition-all flex items-center gap-3"
              >
                {t("hero_cta_shop")}
                <span className="bg-[#D97B66] group-hover:bg-[#C66A55] rounded-full p-2 transition-colors">
                  <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </span>
              </Link>
              <Link
                to="/om-oss"
                data-testid="hero-learn-cta"
                className="border border-[#2C302E]/15 hover:border-[#2C302E]/40 text-[#2C302E] rounded-full px-7 py-3 font-medium transition-colors"
              >
                {t("hero_cta_learn")}
              </Link>
            </div>

            {/* Slide indicators */}
            <div className="mt-10 flex items-center gap-2.5" data-testid="hero-slide-indicators">
              {slides.map((s, i) => (
                <button
                  key={s.id}
                  data-testid={`hero-slide-dot-${i}`}
                  onClick={() => setActive(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i === active
                      ? "w-10 bg-[#D97B66]"
                      : "w-1.5 bg-[#2C302E]/25 hover:bg-[#2C302E]/50"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Right — "Now showing" product card */}
          <div className="hidden lg:block">
            <div className="relative h-[420px]">
              {slides.map((p, i) => {
                const title = lang === "en" ? p.title_en : p.title_no;
                return (
                  <Link
                    key={p.id}
                    to={`/produkt/${p.slug}`}
                    data-testid={`hero-product-card-${i}`}
                    aria-hidden={i !== active}
                    className={`absolute inset-0 group transition-all duration-700 ease-out ${
                      i === active
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 translate-y-6 pointer-events-none"
                    }`}
                  >
                    <div className="h-full bg-white border border-[#E5E2DC] rounded-3xl overflow-hidden shadow-[0_20px_60px_-20px_rgba(44,48,46,0.18)] flex flex-col">
                      <div className="relative aspect-[4/3] bg-[#F9F8F6] overflow-hidden">
                        <img
                          src={p.image_url}
                          alt={title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <span className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#D97B66]">
                          {lang === "en" ? "Now showing" : "Vises nå"}
                        </span>
                      </div>
                      <div className="p-5 flex-1 flex flex-col">
                        <p className="font-heading text-lg text-[#2C302E] font-medium leading-snug line-clamp-2">
                          {title}
                        </p>
                        <div className="mt-auto pt-4 flex items-center justify-between">
                          <span className="font-heading text-xl text-[#2C302E] font-medium">
                            {p.price_nok} NOK
                          </span>
                          <span className="text-sm text-[#D97B66] inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                            {lang === "en" ? "View" : "Se"}
                            <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
